import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { FaEye, FaEyeSlash } from 'react-icons/fa'
import { Modal, InputGroup, Button } from '../../../../UI'
import { createMerchant, checkMerchantEmail } from '../../../../store/actions/merchants'
import Container from './styles'

const MerchantForm = ({ show, handleClose, editData = null }) => {
  const dispatch = useDispatch()
  const [currentStep, setCurrentStep] = useState(1)
  const [showPassword, setShowPassword] = useState(false)
  const isEditMode = !!editData
  
  // Form data
  const [formData, setFormData] = useState({
    companyName: editData?.companyName || '',
    email: editData?.email || '',
    phone: editData?.phone || '',
    address: editData?.address || '',
    contactPerson: editData?.contactPerson || '',
    password: '',
    confirmPassword: '',
  })

  // Error states
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)

  // Update form data when editData changes
  useEffect(() => {
    if (editData) {
      setFormData({
        companyName: editData.companyName || '',
        email: editData.email || '',
        phone: editData.phone || '',
        address: editData.address || '',
        contactPerson: editData.contactPerson || '',
        password: '',
        confirmPassword: '',
      })
      setCurrentStep(1)
      setErrors({})
    }
  }, [editData, show])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const validateStep1 = async () => {
    const newErrors = {}

    if (!formData.companyName?.trim()) {
      newErrors.companyName = 'Company name is required'
    }

    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid'
    } else if (!isEditMode || formData.email !== editData.email) {
      // Check if email exists only if it's create mode or email changed
      const exists = await checkMerchantEmail(formData.email)
      if (exists) {
        newErrors.email = 'Email already exists'
      }
    }

    if (!formData.contactPerson?.trim()) {
      newErrors.contactPerson = 'Contact person is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validateStep2 = () => {
    const newErrors = {}

    if (!formData.phone?.trim()) {
      newErrors.phone = 'Phone number is required'
    }

    if (!formData.address?.trim()) {
      newErrors.address = 'Address is required'
    }

    // Password validation - only required in create mode
    if (!isEditMode) {
      if (!formData.password) {
        newErrors.password = 'Password is required'
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters'
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm password'
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = async () => {
    if (await validateStep1()) {
      setCurrentStep(2)
    }
  }

  const handleBack = () => {
    setCurrentStep(1)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateStep2()) return

    setLoading(true)

    const { confirmPassword, ...merchantData } = formData
    
    const result = await dispatch(createMerchant(merchantData))

    setLoading(false)

    if (result.success) {
      handleClose()
      resetForm()
    }
  }

  const resetForm = () => {
    setFormData({
      companyName: '',
      email: '',
      phone: '',
      address: '',
      contactPerson: '',
      password: '',
      confirmPassword: '',
    })
    setErrors({})
    setCurrentStep(1)
  }

  const onClose = () => {
    handleClose()
    resetForm()
  }

  return (
    <Modal showModal={show} onClose={onClose} showCloseBtn={true}>
      <Container>
        <div className="form--container">
          <header>
            <h2>{isEditMode ? 'Edit Merchant' : 'Add New Merchant'}</h2>
            <p>{currentStep === 1 ? 'Enter merchant business information' : 'Enter contact details'}</p>
          </header>

          <div className="progress-indicator">
            <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>
              <div className="step-number">1</div>
              <span>Business Info</span>
            </div>
            <div className="step-line"></div>
            <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>
              <div className="step-number">2</div>
              <span>Contact & Security</span>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {currentStep === 1 ? (
              <div className="form-step">
                <InputGroup
                  label="Company Name"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder="Enter merchant company name"
                  required
                />
                {errors.companyName && (
                  <div className="error-message">{errors.companyName}</div>
                )}

                <InputGroup
                  label="Contact Person"
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={handleChange}
                  placeholder="Enter contact person name"
                  required
                />
                {errors.contactPerson && (
                  <div className="error-message">{errors.contactPerson}</div>
                )}

                <InputGroup
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  required
                />
                {errors.email && (
                  <div className="error-message">{errors.email}</div>
                )}

                <Button type="button" onClick={handleNext} full>
                  Next
                </Button>
              </div>
            ) : (
              <div className="form-step">
                <InputGroup
                  label="Phone Number"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter phone number"
                  required
                />
                {errors.phone && (
                  <div className="error-message">{errors.phone}</div>
                )}

                <div className="input-group">
                  <label>Address</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Enter business address"
                    rows="3"
                    required
                  />
                </div>
                {errors.address && (
                  <div className="error-message">{errors.address}</div>
                )}

                <div className="input-group">
                  <label>Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Enter password (min 8 characters)"
                      required={!isEditMode}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
                {errors.password && (
                  <div className="error-message">{errors.password}</div>
                )}

                <div className="input-group">
                  <label>Confirm Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm password"
                      required={!isEditMode}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
                {errors.confirmPassword && (
                  <div className="error-message">{errors.confirmPassword}</div>
                )}

                <div className="button-row">
                  <Button type="button" onClick={handleBack} className="btn-secondary">
                    Back
                  </Button>
                  <Button type="submit" disabled={loading}>
                    {loading ? 'Creating...' : 'Create Merchant'}
                  </Button>
                </div>
              </div>
            )}
          </form>
        </div>
      </Container>
    </Modal>
  )
}

export default MerchantForm
